import type { Moment } from "moment";
import moment from "moment";

export const COMPARES = [
  {
    id: "mom",
    name: "Previous Period",
  },
  {
    id: "yoy",
    name: "Previous Year",
  },
  {
    id: "custom",
    name: "Custom",
  },
] as const;

export const GROUPS = [
  {
    id: "day",
    name: "Day",
    defaultPeriod: "last30",
    getPeriodEnd(includeCurrentPeriod: boolean) {
      const now = moment();
      return includeCurrentPeriod ? now.endOf("day") : now.subtract(1, "day").endOf("day");
    },
    compares: {
      mom: {
        getPreviousPeriod(start: Moment, end: Moment) {
          const preEnd = start.clone().subtract(1, "days");
          const preStart = preEnd.clone().subtract(end.diff(start, "days"), "days");
          return {
            preEnd,
            preStart,
          };
        },
      },
      yoy: {
        getPreviousPeriod(start: Moment, end: Moment) {
          const preStart = start.clone().subtract(1, "year");
          const preEnd = end.clone().subtract(1, "year");
          return {
            preEnd,
            preStart,
          };
        },
      },
    },
    periods: [
      {
        id: "lastMonth",
        name: "Last Month",
        group: "day",
        getRelativeEnd() {
          return moment().startOf("month").subtract(1, "month").endOf("month");
        },
        getRelativeStart(end: Moment) {
          return end.clone().startOf("month");
        },
      },
      {
        id: "lastWeek",
        name: "Last Week",
        group: "day",
        getRelativeEnd() {
          return moment().startOf("week").subtract(1, "week").endOf("week");
        },
        getRelativeStart(end: Moment) {
          return end.clone().startOf("week");
        },
      },
      {
        id: "last7",
        name: "Last 7 days",
        group: "day",
        getRelativeEnd(includeCurrentPeriod: boolean) {
          return includeCurrentPeriod ? moment().endOf("day") : moment().subtract(1, "day").endOf("day");
        },

        getRelativeStart(end: Moment) {
          return end.clone().subtract(6, "days");
        },
      },
      {
        id: "last14",
        name: "Last 14 days",
        group: "day",
        getRelativeEnd(includeCurrentPeriod: boolean) {
          return includeCurrentPeriod ? moment().endOf("day") : moment().subtract(1, "day").endOf("day");
        },
        getRelativeStart(end: Moment) {
          return end.clone().subtract(13, "days");
        },
      },
      {
        id: "last30",
        name: "Last 30 days",
        group: "day",
        getRelativeEnd(includeCurrentPeriod: boolean) {
          return includeCurrentPeriod ? moment().endOf("day") : moment().subtract(1, "day").endOf("day");
        },
        getRelativeStart(end: Moment) {
          return end.clone().subtract(29, "days");
        },
      },
      {
        id: "month",
        name: "Month to Date",
        group: "day",
        monthCount: 1,
        getRelativeEnd(includeCurrentPeriod: boolean) {
          return includeCurrentPeriod ? moment().endOf("day") : moment().subtract(1, "day").endOf("day");
        },
        getRelativeStart(end: Moment) {
          return end.clone().startOf("month");
        },
      },
      { id: "custom", name: "Custom" },
    ],
  },
  {
    id: "week",
    name: "Week",
    defaultPeriod: "last12Weeks",
    getPeriodEnd(includeCurrentPeriod: boolean) {
      const now = moment();
      const currentWeekStart = now.clone().startOf("week");
      const currentWeekEnd = currentWeekStart.clone().add(6, "days").endOf("day");
      if (includeCurrentPeriod) {
        return moment.min(currentWeekEnd, now.clone().endOf("day"));
      }
      return currentWeekStart.clone().subtract(1, "week").add(6, "days").endOf("day");
    },
    compares: {
      mom: {
        getPreviousPeriod(start: Moment, end: Moment) {
          const preEnd = start.clone().subtract(1, "days");
          const preStart = preEnd.clone().subtract(end.diff(start, "weeks"), "weeks");
          return {
            preEnd,
            preStart,
          };
        },
      },
      yoy: {
        getPreviousPeriod(start: Moment, end: Moment) {
          const preEnd = end.clone().subtract(1, "year");
          const preStart = start.clone().subtract(1, "year");
          return {
            preEnd,
            preStart,
          };
        },
      },
    },
    periods: [
      {
        id: "last12Weeks",
        name: "Last 12 weeks",
        group: "week",
        getRelativeStart(end: Moment) {
          return end.clone().subtract(11, "weeks").startOf("week");
        },
      },
      {
        id: "last26Weeks",
        name: "Last 26 weeks",
        group: "week",
        getRelativeStart(end: Moment) {
          return end.clone().subtract(25, "weeks").startOf("week");
        },
      },
      { id: "custom", name: "Custom" },
    ],
  },
  {
    id: "month",
    name: "Month",
    defaultPeriod: "last6Months",
    getPeriodEnd(includeCurrentPeriod: boolean) {
      const now = moment();
      return includeCurrentPeriod ? now.endOf("month") : now.subtract(1, "month").endOf("month");
    },
    compares: {
      mom: {
        getPreviousPeriod(start: Moment, end: Moment) {
          const preEnd = start.clone().subtract(1, "days");
          const preStart = preEnd.clone().subtract(end.diff(start, "months"), "months").startOf("month");
          return {
            preEnd,
            preStart,
          };
        },
      },
      yoy: {
        getPreviousPeriod(start: Moment, end: Moment) {
          const preEnd = end.clone().subtract(1, "year").endOf("month");
          const preStart = start.clone().subtract(1, "year").startOf("month");
          return {
            preEnd,
            preStart,
          };
        },
      },
    },
    periods: [
      {
        id: "last6Months",
        name: "Last 6 months",
        group: "month",
        monthCount: 6,
        getRelativeStart(end: Moment) {
          const months = 6;
          return end
            .clone()
            .subtract(months - 1, "months")
            .startOf("month");
        },
      },
      {
        id: "last12Months",
        name: "Last 12 months",
        group: "month",
        monthCount: 12,
        getRelativeStart(end: Moment) {
          const months = 12;
          return end
            .clone()
            .subtract(months - 1, "months")
            .startOf("month");
        },
      },
      { id: "custom", name: "Custom" },
    ],
  },
] as const;

export const DEFAULTS = {
  compare: COMPARES[0], // mom
  group: GROUPS[0], // day
};
